import React, { useEffect } from "react";
import Aos from "aos";
import "aos/dist/aos.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faChartLine,
  faCogs,
  faFileAlt,
  faExclamationTriangle,
  faHardHat,
  faClock,
  faGlobe,
  faSearchLocation,
  faBullhorn,
  faBriefcase,
  faStar,
  faCalculator,
  faUsers,
  faLink,
} from "@fortawesome/free-solid-svg-icons";

// Placeholder images (replace with actual paths in your project)
import mobileAppImg from "../assets/Images/Services/digital-marketing-1725340_1280.jpg";
import digitalMarketingImg from "../assets/Images/Services/digital-marketing-4229635_1280.jpg";
import customSoftwareImg from "../assets/Images/Services/web-design-3411373_1280.jpg";

// LazyLoad component (assuming it's defined elsewhere)
import LazyLoad from "./LazyLoad";

const ServiceCard = ({ title, description, items, image, bgClass, aosDelay, icon }) => (
  <div className={`my-5 service-container ${bgClass}`} data-aos="fade-up" data-aos-delay={aosDelay}>
    <h3 className="text-black text-center fw-bold pt-5">{title}</h3>
    <div className="d-flex service-cards py-5 align-items-center">
      <div className="service-bg w-50">
        <img src={image} alt={title} className="img-fluid service-img" />
      </div>
      <div className="service-card my-5 px-4">
        <p className="fs-5 text-dark mb-4">{description}</p>
        <dl>
          {items.map((item, index) => (
            <div key={index} className="mb-3">
              <dt className="d-flex align-items-center">
                <FontAwesomeIcon icon={item.icon} className="me-2 text-primary" />
                {item.title}
              </dt>
              <dd className="ms-4">{item.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </div>
);

const OurServices1 = () => {
  useEffect(() => {
    Aos.init({
      duration: 900,
      easing: "ease-out-cubic",
    });
  }, []);

  const services = [
    {
      title: "Mobile App Development – Power in the Palm of Your Hard Hat",
      description:
        "The job site never stops—and neither should your team. Zithas Technologies builds custom mobile apps that give your field crews everything they need to work smarter, safer, and faster, right from their devices.",
      items: [
        {
          title: "Digital Site Diaries & Daily Reports",
          description:
            "Log everything from weather conditions to work completed with voice-to-text input, geo-tagged photos, and real-time syncing to your office systems.",
          icon: faFileAlt,
        },
        {
          title: "Punch Lists & Issue Tracking",
          description:
            "Create, assign, and resolve snags on the go. Teams can flag problems with photos and comments, ensuring nothing falls through the cracks.",
          icon: faExclamationTriangle,
        },
        {
          title: "Toolbox Talks & Safety Compliance",
          description:
            "Deliver daily safety briefings and updates digitally. Record attendance, upload HSE forms, and maintain compliance without paper trails.",
          icon: faHardHat,
        },
        {
          title: "Time & Equipment Tracking",
          description:
            "Monitor clock-ins, machine usage, and downtime in real-time. Improve resource allocation and prevent costly overuse or underreporting.",
          icon: faClock,
        },
        {
          title: "Offline Functionality for Remote Sites",
          description:
            "Work doesn’t stop when the signal drops. Our apps function offline and sync automatically once reconnected—keeping productivity high in low-connectivity zones.",
          icon: faGlobe,
        },
      ],
      image: mobileAppImg,
      bgClass: "bg-white",
      aosDelay: "100",
      icon: faMobileAlt,
    },
    {
      title: "Digital Marketing – Build Trust Before You Break Ground",
      description:
        "Being great at construction isn’t enough—your ideal clients need to find you. Zithas Technologies helps you stand out online with smart, targeted digital campaigns tailored for the construction industry.",
      items: [
        {
          title: "Local SEO That Gets You Found",
          description:
            "Dominate local searches like “construction company near me” with optimised site content, Google Business listings, and citations that boost your visibility.",
          icon: faSearchLocation,
        },
        {
          title: "Targeted PPC for Real Leads",
          description:
            "Run high-converting Google and social ads aimed at developers, architects, and homeowners—focused on quality enquiries, not just clicks.",
          icon: faBullhorn,
        },
        {
          title: "LinkedIn Outreach & B2B Branding",
          description:
            "Connect with decision-makers through professional content, outreach campaigns, and storytelling that builds trust and authority.",
          icon: faBriefcase,
        },
        {
          title: "Reviews & Reputation Management",
          description:
            "Automate review collection and monitor platforms like Google and Checkatrade to showcase client satisfaction and build credibility.",
          icon: faStar,
        },
      ],
      image: digitalMarketingImg,
      bgClass: "bg-grey1",
      aosDelay: "200",
      icon: faChartLine,
    },
    {
      title: "Custom Construction Software – Built Around Your Blueprint",
      description:
        "Every construction business is unique. Zithas Technologies crafts bespoke software solutions that fit your workflows, eliminate inefficiencies, and scale with your growth.",
      items: [
        {
          title: "Tender & Bid Management",
          description:
            "Streamline bid creation, submissions, and deadlines to win contracts faster.",
          icon: faFileAlt,
        },
        {
          title: "Job Costing & Procurement Tracking",
          description:
            "Track real-time costs across labour, materials, and suppliers—stay on budget and avoid delays.",
          icon: faCalculator,
        },
        {
          title: "Construction-Specific CRM",
          description:
            "Manage leads, clients, contractors, and vendors with a centralised, construction-ready system.",
          icon: faUsers,
        },
        {
          title: "Smart Integrations",
          description:
            "Seamlessly link with CAD, BIM, Sage, Xero, and other tools to keep your workflows connected.",
          icon: faLink,
        },
      ],
      image: customSoftwareImg,
      bgClass: "bg-tree2",
      aosDelay: "300",
      icon: faCogs,
    },
  ];

  return (
    <section className="pt-5" id="our-services">
      <div className="custom-auto-container py-md-0">
        <div className="row px-md-2">
          <LazyLoad>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                title={service.title}
                description={service.description}
                items={service.items}
                image={service.image}
                bgClass={service.bgClass}
                aosDelay={service.aosDelay}
                icon={service.icon}
              />
            ))}
          </LazyLoad>
        </div>
      </div>
    </section>
  );
};

export default OurServices1;